import get from 'lodash/get';

import type { CommonImageItemType } from '@/components-biz/ImageSelect';
import { startupArrayToCommaText } from '@/components-biz/ImageSelect';
import { parseContainerDeployConfigObject } from '@/delivery-type-plugin/utils/parseContainerDeployConfigObject';

import { type VolumeClaimTemplateItem, VolumeMountType } from '../../../../capabilities/container-storage/volumeTypes';
import type { ParsedStartupCommandItem } from '../../../../capabilities/maas-system-config/startupCommandsCore';
import {
  type StartupArgDefinition,
  type StartupArgFormRow,
  normalizeStartupArgDefinitions,
} from '../../../../capabilities/startup-command-args/utils';
import { readStoredContainerDeployConfig } from '../../../../platform/container-deploy-config';

/** L2 兼容 re-export；真源在包级 utils */
export { parseContainerDeployConfigObject };

export interface ContainerDeployInstanceSchedulingGpu {
  resourceType?: string;
  vendor?: string;
  device?: string;
  deviceType?: string;
  vendorID?: string;
  deviceID?: string;
  code?: string;
  driverVersion?: string;
  cudaVersion?: string;
}

export interface ContainerDeployInstanceScheduling {
  enabled?: boolean;
  minCpuCores?: number;
  minMemoryGi?: number;
  gpus?: ContainerDeployInstanceSchedulingGpu[];
}

export interface ContainerDeploySystemOverlay {
  security?: Record<string, unknown>;
  devices?: Record<string, unknown>;
  livenessProbe?: Record<string, unknown>;
}

export interface ImageDeployVolumePaths {
  persistent: string[];
  ephemeral: string[];
}

function readImageField(values: Record<string, unknown>, imagePrefix: string, segment: string): unknown {
  const flatPath = imagePrefix ? `${imagePrefix}.${segment}` : segment;
  return get(values, flatPath);
}

function normalizeContainerDeployConfigRaw(raw: unknown): string | undefined {
  if (raw == null || raw === '') return undefined;
  if (typeof raw === 'string') return raw;
  try {
    return JSON.stringify(raw);
  } catch {
    return undefined;
  }
}

/** 从表单值还原镜像项（兼容 Arco 嵌套结构与 legacy 扁平 path） */
export function readContainerImageItemFromFormValues(
  values: Record<string, unknown>,
  imagePrefix = 'basic.image',
): CommonImageItemType | null {
  const nested = get(values, imagePrefix);
  const key = String(readImageField(values, imagePrefix, 'key') ?? readImageField(values, imagePrefix, 'imageKey') ?? '').trim();

  let containerDeployConfig: unknown = readStoredContainerDeployConfig(values);
  if (containerDeployConfig == null || containerDeployConfig === '') {
    containerDeployConfig = readImageField(values, imagePrefix, 'containerDeployConfig');
  }
  if (containerDeployConfig == null || containerDeployConfig === '') {
    const optionMeta = get(values, 'optionMeta') as Record<string, unknown> | undefined;
    const metaByKey = key ? optionMeta?.[key] : undefined;
    const meta = metaByKey && typeof metaByKey === 'object' && !Array.isArray(metaByKey) ? metaByKey : optionMeta?.image;
    if (meta && typeof meta === 'object') {
      containerDeployConfig = (meta as Record<string, unknown>).containerDeployConfig;
    }
  }
  const resolvedContainerDeployConfig = normalizeContainerDeployConfigRaw(containerDeployConfig);

  if (!key && !resolvedContainerDeployConfig) {
    return null;
  }

  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    const row = nested as Record<string, unknown>;
    return {
      ...(row as unknown as CommonImageItemType),
      id: String(row.id ?? row.key ?? key),
      key: String(row.key ?? row.id ?? key),
      containerDeployConfig: normalizeContainerDeployConfigRaw(row.containerDeployConfig) ?? resolvedContainerDeployConfig,
    } as CommonImageItemType;
  }

  return {
    id: key,
    key,
    name: String(readImageField(values, imagePrefix, 'name') ?? readImageField(values, imagePrefix, 'imageName') ?? '').trim(),
    dockerRefUrl: String(readImageField(values, imagePrefix, 'url') ?? readImageField(values, imagePrefix, 'image') ?? '').trim(),
    containerDeployConfig: resolvedContainerDeployConfig,
  } as CommonImageItemType;
}

function parseStartupCommandItemFromRaw(item: unknown): ParsedStartupCommandItem | undefined {
  if (!item || typeof item !== 'object') return undefined;
  const row = item as { command?: unknown; args?: unknown };
  let commandText: string | undefined;
  if (Array.isArray(row.command)) {
    commandText = startupArrayToCommaText(row.command) || undefined;
  } else if (typeof row.command === 'string' && row.command.trim()) {
    commandText = row.command.trim();
  }
  const args = row.args;
  const hasArgs = Array.isArray(args) && args.length > 0;
  if (!commandText && !hasArgs) return undefined;
  return { commandText, args: hasArgs ? args : undefined };
}

function readStartupCommandItem(startup: Record<string, unknown> | undefined): ParsedStartupCommandItem | undefined {
  if (!startup || typeof startup !== 'object') return undefined;
  const candidates = [
    startup.normal,
    startup.single,
    (startup.distributed as Record<string, unknown> | undefined)?.master,
    (startup.primarySecondary as Record<string, unknown> | undefined)?.master,
    (startup.primarySecondary as Record<string, unknown> | undefined)?.worker,
  ];
  for (const item of candidates) {
    const parsed = parseStartupCommandItemFromRaw(item);
    if (parsed) return parsed;
  }
  return undefined;
}

/** 从 containerDeployConfig 解析启动参数定义（供 linkage / 编辑回填约束） */
export function readStartupArgDefinitionsFromDeployConfig(raw?: string | Record<string, unknown> | null): StartupArgDefinition[] {
  const config = parseContainerDeployConfigObject(raw);
  const startupItem = readStartupCommandItem(config.startupCommands as Record<string, unknown> | undefined);
  return normalizeStartupArgDefinitions(startupItem?.args);
}

/** 编辑回填：保留详情参数取值，从 containerDeployConfig 补全必填/可见/可编辑等 _self 元数据 */
export function mergeStartupArgsWithDeployConfigConstraints(
  rows: StartupArgFormRow[],
  rawConfig?: string | Record<string, unknown> | null,
): StartupArgFormRow[] {
  const definitions = readStartupArgDefinitionsFromDeployConfig(rawConfig);
  if (!definitions.length) return rows;

  const defByName = new Map(definitions.map((item) => [item.name, item]));
  return rows.map((row) => {
    const name = String(row.name ?? '').trim();
    const def = name ? defByName.get(name) : undefined;
    if (!def) return row;
    return {
      ...row,
      _self: JSON.stringify(def),
    };
  });
}

/** 将 containerDeployConfig.startupCommands.args 转为带 _self 的表单行 */
export { buildStartupArgFormRowsFromDeployConfigArgs } from '@/delivery-type-plugin/capabilities/maas-system-config/startupCommandsCore';

/** 从 containerDeployConfig 读取启动命令与参数定义源 */
export function readDeployConfigStartupCommand(raw?: string | Record<string, unknown> | null) {
  const config = parseContainerDeployConfigObject(raw);
  return readStartupCommandItem(config.startupCommands as Record<string, unknown> | undefined);
}

export {
  readDeployConfigStartupCommandForRole,
  readStartupCommandForRoleFromStartupRoot,
} from '@/delivery-type-plugin/capabilities/maas-system-config/startupCommandsCore';
export type { ParsedStartupCommandItem } from '@/delivery-type-plugin/capabilities/maas-system-config/startupCommandsCore';

/** 镜像是否携带可应用的 containerDeployConfig 预置段（无预置时不应清场 API 回显字段） */
export function hasContainerDeployConfigContent(raw?: string | Record<string, unknown> | null): boolean {
  if (raw == null || raw === '') return false;
  const config = parseContainerDeployConfigObject(raw);
  if (!Object.keys(config).length) return false;

  const scheduling = normalizeContainerDeployInstanceScheduling(config.instanceScheduling);
  const volumePaths = resolveImageDeployVolumePaths({ containerDeployConfig: raw } as CommonImageItemType);
  const systemOverlay = buildContainerDeploySystemOverlay(config);
  const startup = config.startupCommands;
  const hasStartup =
    startup &&
    typeof startup === 'object' &&
    Object.values(startup as Record<string, unknown>).some((item) => {
      if (!item || typeof item !== 'object') return false;
      const row = item as { command?: unknown; args?: unknown };
      const hasCommand = Array.isArray(row.command)
        ? row.command.length > 0
        : typeof row.command === 'string' && row.command.trim().length > 0;
      const hasArgs = Array.isArray(row.args) && row.args.length > 0;
      return hasCommand || hasArgs;
    });

  return Boolean(
    (Array.isArray(config.apis) && config.apis.length > 0) ||
      (Array.isArray(config.envVars) && config.envVars.length > 0) ||
      hasStartup ||
      scheduling ||
      volumePaths.persistent.length > 0 ||
      volumePaths.ephemeral.length > 0 ||
      systemOverlay,
  );
}

function resolvePersistentVolumeMountPaths(config?: Record<string, unknown> | null): string[] {
  if (!config || typeof config !== 'object') return [];
  const rawMount = config.persistentVolumeMountPath;
  const pathsFromMountField = Array.isArray(rawMount)
    ? (rawMount as unknown[]).map((item) => String(item ?? '').trim()).filter(Boolean)
    : String(rawMount ?? '').trim()
      ? [String(rawMount).trim()]
      : [];
  const pathsFromPlural = Array.isArray(config.persistentVolumeMountPaths)
    ? (config.persistentVolumeMountPaths as unknown[]).map((item) => String(item ?? '').trim()).filter(Boolean)
    : [];
  return pathsFromPlural.length ? pathsFromPlural : pathsFromMountField;
}

function resolveTemporaryVolumeMountPaths(config?: Record<string, unknown> | null): string[] {
  if (!config || typeof config !== 'object') return [];
  const rawMount = config.temporaryVolumeMountPath;
  const pathsFromMountField = Array.isArray(rawMount)
    ? (rawMount as unknown[]).map((item) => String(item ?? '').trim()).filter(Boolean)
    : String(rawMount ?? '').trim()
      ? [String(rawMount).trim()]
      : [];
  const pathsFromPlural = Array.isArray(config.temporaryVolumeMountPaths)
    ? (config.temporaryVolumeMountPaths as unknown[]).map((item) => String(item ?? '').trim()).filter(Boolean)
    : [];
  return pathsFromPlural.length ? pathsFromPlural : pathsFromMountField;
}

/** 从镜像 containerDeployConfig 解析持久/临时卷挂载路径 */
export function resolveImageDeployVolumePaths(imageItem?: CommonImageItemType | null): ImageDeployVolumePaths {
  const config = parseContainerDeployConfigObject(imageItem?.containerDeployConfig);
  return {
    persistent: resolvePersistentVolumeMountPaths(config),
    ephemeral: resolveTemporaryVolumeMountPaths(config),
  };
}

function normalizeCapabilitiesForApi(values: string[]): string[] {
  const normalized = values.map((item) => String(item ?? '').trim()).filter(Boolean);
  if (normalized.includes('ALL')) return ['ALL'];
  return normalized;
}

function normalizeContainerDeployFlag(value: unknown): boolean {
  if (typeof value === 'boolean') return value;
  if (value && typeof value === 'object' && 'enabled' in (value as object)) {
    return !!(value as { enabled?: boolean }).enabled;
  }
  return false;
}

/** devices / sharedMemory / security → API system 子树 */
export function buildContainerDeploySystemOverlay(config?: Record<string, unknown>): ContainerDeploySystemOverlay | undefined {
  if (!config) return undefined;

  const devicesCfg = config.devices as Record<string, unknown> | undefined;
  const securityCfg = config.security as Record<string, unknown> | undefined;
  const sharedMemoryCfg = config.sharedMemory as { sizeLimit?: string } | undefined;

  const security: Record<string, unknown> = {};
  if (normalizeContainerDeployFlag(securityCfg?.seccompUnconfined)) {
    security.seccompUnconfined = { enabled: true };
  }
  if (normalizeContainerDeployFlag(securityCfg?.netAdmin)) {
    security.netAdmin = { enabled: true };
  }
  if (normalizeContainerDeployFlag(securityCfg?.privileged)) {
    security.privileged = { enabled: true };
  }

  const capabilitiesRaw = securityCfg?.capabilities as { drop?: string[]; add?: string[] } | undefined;
  const drop = normalizeCapabilitiesForApi(capabilitiesRaw?.drop || []);
  const add = normalizeCapabilitiesForApi(capabilitiesRaw?.add || []);
  if (drop.length || add.length) {
    security.capabilities = {
      ...(drop.length ? { drop } : {}),
      ...(add.length ? { add } : {}),
    };
  }

  const runCfg = securityCfg?.run as { runAsUser?: unknown; runAsGroup?: unknown } | undefined;
  const run: Record<string, number> = {};
  const appendRunAs = (key: 'runAsUser' | 'runAsGroup', value: unknown) => {
    const num = Number(value);
    if (Number.isInteger(num) && num >= 0 && num <= 10000) {
      run[key] = num;
    }
  };
  appendRunAs('runAsUser', runCfg?.runAsUser);
  appendRunAs('runAsGroup', runCfg?.runAsGroup);
  if (Object.keys(run).length) {
    security.run = run;
  }

  const devices: Record<string, unknown> = {};
  const virtualDevices = devicesCfg?.virtualDevices as string[] | undefined;
  if (Array.isArray(virtualDevices) && virtualDevices.length) {
    for (const name of virtualDevices) {
      const deviceName = String(name ?? '').trim();
      if (deviceName) {
        devices[deviceName] = {};
      }
    }
  }

  const sharedMemorySize = String(sharedMemoryCfg?.sizeLimit ?? '').trim();
  if (sharedMemorySize) {
    devices.sharedMemory = { size: sharedMemorySize };
  }

  const manualMount = normalizeContainerDeployFlag(devicesCfg?.manualMount);
  const mountItems = devicesCfg?.mounts as { local?: string; mount?: string }[] | undefined;
  if (manualMount && Array.isArray(mountItems) && mountItems.length) {
    const mounts = mountItems
      .map((item) => ({
        hostPath: String(item?.local ?? '').trim(),
        mountPath: String(item?.mount ?? '').trim(),
      }))
      .filter((item) => item.hostPath && item.mountPath);
    if (mounts.length) {
      devices.mounts = mounts;
    }
  }

  const overlay: ContainerDeploySystemOverlay = {};
  if (Object.keys(security).length) overlay.security = security;
  if (Object.keys(devices).length) overlay.devices = devices;

  const livenessProbe = healthCheckToK8sLivenessProbe(config.healthCheck as Record<string, unknown> | undefined);
  if (livenessProbe) overlay.livenessProbe = livenessProbe;

  return Object.keys(overlay).length ? overlay : undefined;
}

function resolveHttpProbeScheme(httpMethod?: string): 'HTTP' | 'HTTPS' {
  const method = String(httpMethod ?? '')
    .trim()
    .toUpperCase();
  return method === 'HTTPS' ? 'HTTPS' : 'HTTP';
}

function parseExecProbeCommand(command?: string): string[] | undefined {
  const raw = String(command ?? '').trim();
  if (!raw) return undefined;
  return raw.split(/\s+/).filter(Boolean);
}

/** healthCheck → K8s livenessProbe（enabled 为 false 时不写入） */
function healthCheckToK8sLivenessProbe(healthCheck?: Record<string, unknown>): Record<string, unknown> | undefined {
  if (!healthCheck || typeof healthCheck !== 'object') return undefined;
  if (!healthCheck.enabled) return undefined;

  const probeType = String(healthCheck.defaultProbeType ?? 'http')
    .trim()
    .toLowerCase();
  const probe: Record<string, unknown> = {};

  if (probeType === 'exec') {
    const command = parseExecProbeCommand(healthCheck.defaultCommand as string | undefined);
    if (!command?.length) return undefined;
    probe.exec = { command };
  } else if (probeType === 'tcp') {
    const port = Number(healthCheck.defaultPort);
    if (!Number.isFinite(port) || port <= 0) return undefined;
    probe.tcpSocket = { port };
  } else {
    const port = Number(healthCheck.defaultPort);
    if (!Number.isFinite(port) || port <= 0) return undefined;
    const path = String(healthCheck.defaultPath ?? '').trim() || '/healthz';
    probe.httpGet = {
      path,
      port,
      scheme: resolveHttpProbeScheme(healthCheck.httpMethod as string | undefined),
    };
  }

  const appendTiming = (key: 'initialDelaySeconds' | 'periodSeconds' | 'timeoutSeconds' | 'failureThreshold') => {
    const value = Number(healthCheck[key]);
    if (Number.isFinite(value) && value >= 0) {
      probe[key] = value;
    }
  };
  appendTiming('initialDelaySeconds');
  appendTiming('periodSeconds');
  appendTiming('timeoutSeconds');
  appendTiming('failureThreshold');

  return probe;
}

/** instanceScheduling 归一化，供 compute 最小资源约束 */
export function normalizeContainerDeployInstanceScheduling(raw: unknown): ContainerDeployInstanceScheduling | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const scheduling = raw as ContainerDeployInstanceScheduling;
  const minCpuCores = Number(scheduling.minCpuCores);
  const minMemoryGi = Number(scheduling.minMemoryGi);
  const gpus = Array.isArray(scheduling.gpus)
    ? scheduling.gpus
        .map((item) => ({
          resourceType: String(item.resourceType ?? '').trim() || undefined,
          vendor: String(item.vendor ?? '').trim() || undefined,
          device: String(item.device ?? '').trim() || undefined,
          deviceType: String(item.deviceType ?? '').trim() || undefined,
          vendorID: String(item.vendorID ?? '').trim() || undefined,
          deviceID: String(item.deviceID ?? '').trim() || undefined,
          code: String(item.code ?? '').trim() || undefined,
          driverVersion: String(item.driverVersion ?? '').trim() || undefined,
          cudaVersion: String(item.cudaVersion ?? '').trim() || undefined,
        }))
        .filter((item) => item.vendorID || item.deviceID || item.vendor || item.device)
    : undefined;
  const hasConstraints =
    (Number.isFinite(minCpuCores) && minCpuCores > 0) || (Number.isFinite(minMemoryGi) && minMemoryGi > 0) || !!gpus?.length;
  if (!hasConstraints && scheduling.enabled !== true) return undefined;

  return {
    enabled: scheduling.enabled === false ? false : scheduling.enabled === true ? true : hasConstraints,
    ...(Number.isFinite(minCpuCores) && minCpuCores > 0 ? { minCpuCores } : {}),
    ...(Number.isFinite(minMemoryGi) && minMemoryGi > 0 ? { minMemoryGi } : {}),
    ...(gpus?.length ? { gpus } : {}),
  };
}

type ImagePresetVolumeItem = VolumeClaimTemplateItem & { _fromImage?: boolean };

/** 镜像挂载路径 → 容器实例 volumes 预置行（临时卷在前，持久卷在后） */
export function buildImageDeployVolumePresets(paths: ImageDeployVolumePaths, containerName = ''): ImagePresetVolumeItem[] {
  const items: ImagePresetVolumeItem[] = [];
  let index = 0;

  const pushItem = (mountPath: string, kind: 'persistent' | 'ephemeral') => {
    items.push({
      type: 'VolumeClaimTemplate',
      volumeName: `image-${kind}-${index}`,
      accessModes: ['ReadWriteOnce'],
      volumeCapacity: 1,
      reclaimPolicy: 'Delete',
      volumeMounts: [
        {
          containerName,
          mountType: VolumeMountType.ReadWriteOnce,
          mountPath,
        },
      ],
      _fromImage: true,
    });
    index += 1;
  };

  paths.ephemeral.forEach((mountPath) => pushItem(mountPath, 'ephemeral'));
  paths.persistent.forEach((mountPath) => pushItem(mountPath, 'persistent'));

  return items;
}

/** 镜像预置卷命名：`image-ephemeral-0` / `image-persistent-1` */
const IMAGE_PRESET_VOLUME_NAME_PATTERN = /^image-(ephemeral|persistent)-\d+$/;

/** 判断卷是否来自镜像 containerDeployConfig 预置（API 回填补标；换镜像时整表清空） */
export function isImagePresetVolume(item: unknown): boolean {
  if (!item || typeof item !== 'object') return false;
  const row = item as { _fromImage?: boolean; volumeName?: string };
  if (row._fromImage) return true;
  const name = String(row.volumeName ?? '').trim();
  return IMAGE_PRESET_VOLUME_NAME_PATTERN.test(name);
}

/** 编辑回填：保留详情 env 取值，从 containerDeployConfig 补全必填/可见等约束元数据 */
export function mergeEnvVarsWithDeployConfigConstraints(
  envRows: { name: string; value: string; _self?: string }[],
  rawConfig?: string | Record<string, unknown> | null,
): { name: string; value: string; _self?: string }[] {
  const config = parseContainerDeployConfigObject(rawConfig);
  const definitions = Array.isArray(config.envVars) ? config.envVars : [];
  if (!definitions.length) return envRows;

  const defByKey = new Map<string, Record<string, unknown>>();
  definitions.forEach((item) => {
    if (!item || typeof item !== 'object') return;
    const row = item as Record<string, unknown>;
    const key = String(row.key ?? row.name ?? '').trim();
    if (key) defByKey.set(key, row);
  });

  return envRows.map((row) => {
    const def = defByKey.get(row.name);
    if (!def) return row;
    const meta = {
      key: row.name,
      name: row.name,
      description: def.description,
      required: def.required,
      editable: def.editable,
      visible: def.visible,
      custom: false,
    };
    return {
      ...row,
      _self: JSON.stringify(meta),
    };
  });
}
